import type { SortPayload } from 'react-admin';

import { asDisplayString, looselyEquals } from '@/formatting/text';

/**
 * Pagination, filtrage et tri **cote client**.
 *
 * Plusieurs routes de liste de l'API renvoient `{items: [...]}` en entier, sans
 * `limit`, sans `offset` et sans total : `/feeding-sessions`,
 * `/growth/measurements`, `/diversification/foods`, `/stock-movements`,
 * `/storage-locations`, `/babies`.
 *
 * Deux issues possibles, et une seule honnete. Fabriquer une pagination serveur
 * inexistante — en devinant un total, ou en tronquant sans le dire — donnerait
 * des tableaux qui affichent « 1-10 sur 10 » alors que la collection en compte
 * cinquante. Ce module fait donc l'autre choix : il pagine ce qui est deja en
 * memoire, et le README comme les descripteurs le declarent explicitement.
 *
 * La limite est reelle et bornee : ces collections sont portees par un profil
 * bebe, jamais par la base entiere. Elle cesserait de tenir si l'API exposait un
 * jour des collections globales — auquel cas la reponse est une pagination
 * serveur cote backend, pas un contournement ici.
 */

/** Une valeur de filtre `undefined`, `null` ou chaine vide ne filtre rien. */
const isBlank = (value: unknown): boolean =>
  value === undefined || value === null || value === '';

/**
 * Comparaison d'une valeur d'enregistrement a une valeur de filtre.
 *
 * Le texte est compare en **sous-chaine insensible a la casse** — c'est ce
 * qu'attend quiconque tape dans un champ de recherche. Les autres types sont
 * compares par egalite apres normalisation en chaine : un `true` coche dans un
 * filtre booleen arrive parfois en `'true'` depuis la barre de filtres.
 */
const matchesValue = (recordValue: unknown, filterValue: unknown): boolean => {
  if (typeof filterValue === 'string' && typeof recordValue === 'string') {
    return recordValue.toLocaleLowerCase().includes(filterValue.toLocaleLowerCase());
  }

  if (Array.isArray(recordValue)) {
    return recordValue.some((entry) => matchesValue(entry, filterValue));
  }

  if (recordValue === undefined || recordValue === null) {
    return false;
  }

  return looselyEquals(recordValue, filterValue);
};

/**
 * Applique les filtres qui ne sont pas pris en charge par le serveur.
 *
 * Les cles absentes de l'enregistrement ne le rejettent pas silencieusement :
 * elles le rejettent, ce qui est le comportement attendu d'un filtre.
 */
export const applyClientFilters = <T extends Record<string, unknown>>(
  records: readonly T[],
  filters: Readonly<Record<string, unknown>>,
  ignoredKeys: readonly string[] = [],
): T[] => {
  const entries = Object.entries(filters).filter(
    ([key, value]) => !isBlank(value) && !ignoredKeys.includes(key),
  );

  if (entries.length === 0) {
    return [...records];
  }

  return records.filter((record) =>
    entries.every(([key, value]) => matchesValue(record[key], value)),
  );
};

/**
 * Compare deux valeurs pour le tri.
 *
 * Les absents vont **toujours en fin de liste**, quel que soit le sens du tri :
 * une colonne triee par date ne doit pas commencer par une page de cases vides.
 */
const compareValues = (left: unknown, right: unknown): number => {
  const leftMissing = left === undefined || left === null || left === '';
  const rightMissing = right === undefined || right === null || right === '';

  if (leftMissing && rightMissing) {
    return 0;
  }

  if (leftMissing) {
    return 1;
  }

  if (rightMissing) {
    return -1;
  }

  if (typeof left === 'number' && typeof right === 'number') {
    return left - right;
  }

  if (typeof left === 'boolean' && typeof right === 'boolean') {
    return Number(left) - Number(right);
  }

  // Les instants de l'API sont en ISO 8601, donc deja ordonnables comme des
  // chaines. `localeCompare` avec `numeric` traite en plus « lot 2 » avant
  // « lot 10 », ce qu'un tri lexical ferait a l'envers.
  return asDisplayString(left).localeCompare(asDisplayString(right), 'fr', {
    numeric: true,
    sensitivity: 'base',
  });
};

/**
 * Trie une copie de la collection.
 *
 * Le sens `DESC` inverse le comparateur **sans** deplacer les absents, qui
 * restent en fin de liste : c'est pour cela que l'inversion s'applique au
 * resultat de `compareValues` et non a l'ordre des arguments.
 */
export const applyClientSort = <T extends Record<string, unknown>>(
  records: readonly T[],
  sort: SortPayload | undefined,
): T[] => {
  if (sort === undefined || sort.field === '') {
    return [...records];
  }

  const direction = sort.order === 'DESC' ? -1 : 1;

  return [...records].sort((left, right) => {
    const leftValue = left[sort.field];
    const rightValue = right[sort.field];
    const leftMissing = leftValue === undefined || leftValue === null || leftValue === '';
    const rightMissing = rightValue === undefined || rightValue === null || rightValue === '';

    if (leftMissing !== rightMissing) {
      return leftMissing ? 1 : -1;
    }

    return direction * compareValues(leftValue, rightValue);
  });
};

export interface PaginationRequest {
  readonly page: number;
  readonly perPage: number;
}

/**
 * Decoupe la page demandee.
 *
 * Une page au-dela de la fin rend un tableau vide et **le total reel** : React
 * Admin s'en sert pour revenir a la derniere page existante au lieu d'afficher
 * une liste vide sans explication.
 */
export const applyClientPagination = <T>(
  records: readonly T[],
  { page, perPage }: PaginationRequest,
): { data: T[]; total: number } => {
  const safePerPage = perPage > 0 ? perPage : records.length;
  const safePage = page > 0 ? page : 1;
  const start = (safePage - 1) * safePerPage;

  return {
    data: records.slice(start, start + safePerPage),
    total: records.length,
  };
};
